import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Component, ErrorInfo, ReactNode } from "react";

import { captureException } from "@/shared/lib/sentry";

import { Button } from "./button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  // Text props for headless architecture
  titleDevelopment?: string;
  titleProduction?: string;
  messageDevelopment?: string;
  messageProduction?: string;
  tryAgainText?: string;
  reloadPageText?: string;
  contactSupportText?: string;
  errorDetailsText?: string;
  componentStackText?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[NAVIGATION] ErrorBoundary caught error:', error);
    console.error('[NAVIGATION] ErrorBoundary errorInfo:', errorInfo);
    this.setState({
      error,
      errorInfo,
    });

    // Capture error in Sentry
    captureException(error, {
      source: "GlobalErrorBoundary",
      componentStack: errorInfo.componentStack,
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // Log error in development
    if (import.meta.env.MODE === "development") {
      console.error("Error caught by boundary:", error);
      console.error("Component stack:", errorInfo.componentStack);
    }
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDevelopment = import.meta.env.MODE === "development";
      const { error, errorInfo } = this.state;

      return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    {isDevelopment 
                      ? (this.props.titleDevelopment || "Application Error") 
                      : (this.props.titleProduction || "Something went wrong")}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {isDevelopment
                        ? (this.props.messageDevelopment || "An error occurred in the application. Check the console for details.")
                        : (this.props.messageProduction || "We're sorry, but something unexpected happened. Please try again.")}
                    </p>
                  </div>

                  {isDevelopment && error && (
                    <div className="mt-4 p-4 bg-red-50 rounded-md text-left">
                      <h4 className="text-sm font-medium text-red-800 mb-2">{this.props.errorDetailsText || "Error Details:"}:</h4>
                      <pre className="text-xs text-red-700 whitespace-pre-wrap break-words">
                        {error.toString()}
                      </pre>
                      {errorInfo && (
                        <details className="mt-2">
                          <summary className="text-xs text-red-800 cursor-pointer">
                            {this.props.componentStackText || "Component Stack"}
                          </summary>
                          <pre className="text-xs text-red-700 whitespace-pre-wrap break-words mt-1">
                            {errorInfo.componentStack}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-5 sm:mt-6 space-y-3">
                <Button
                  type="button"
                  className="w-full"
                  onClick={this.handleRetry}
                >
                  {this.props.tryAgainText || "Try Again"}
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={this.handleReload}
                >
                  {this.props.reloadPageText || "Reload Page"}
                </Button>

                {!isDevelopment && (
                  <div className="text-center">
                    <p className="text-xs text-gray-500">
                      {this.props.contactSupportText || "If this problem persists, please contact support."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}